import React, { useState, useEffect } from 'react';
import { ArrowRightLeft, Search, Filter, ChevronDown } from 'lucide-react';
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../ui/table";
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../ui/dialog";
import { toast } from "sonner";

const StockTransfer = ({ axiosInstance }) => {
    // --- State ---
    const [warehouses, setWarehouses] = useState([]);
    const [sourceWarehouse, setSourceWarehouse] = useState('');
    const [destWarehouse, setDestWarehouse] = useState('');
    const [transferDate, setTransferDate] = useState(new Date().toISOString().split('T')[0]);
    const [itemType, setItemType] = useState('Machine'); // Default to first tab
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('alphabetical');
    const [selectedBrand, setSelectedBrand] = useState('all'); // Add brand filter state
    const [availableBrands, setAvailableBrands] = useState([]); // State for extracted brands
    
    const [availableStock, setAvailableStock] = useState([]); 
    
    // Modal State
    const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [transferQty, setTransferQty] = useState(1);
    
    const [selectedSerials, setSelectedSerials] = useState([]); // Array of selected serial numbers/ids
    const [transferList, setTransferList] = useState([]); // List of items to transfer

    // Transition State
    const [isTransitioning, setIsTransitioning] = useState(false);

    // --- Effects ---
    useEffect(() => {
        if (axiosInstance) {
            fetchWarehouses();
        }
    }, [axiosInstance]);

    // Fetch stock when filters change (Source Warehouse, Item Type, Search)
    useEffect(() => {
        if (sourceWarehouse) {
            fetchStock();
        } else {
            setAvailableStock([]);
            setAvailableBrands([]);
        }
    }, [sourceWarehouse, itemType, searchQuery, sortBy, selectedBrand]);

    // --- Data Fetching ---
    const fetchWarehouses = async () => {
        try {
            const response = await axiosInstance.get('/inventory/warehouses');
            // Add mock PIC and Contact if missing
            const enrichedWarehouses = response.data.map(w => ({
                ...w,
                pic: w.pic || 'Admin',
                contact: w.contact || 'N/A'
            }));
            setWarehouses(enrichedWarehouses);
        } catch (error) {
            console.error("Error fetching warehouses:", error);
            // Fallback mock data if API fails or is empty
            setWarehouses([
                { id: 1, name: 'Main Warehouse', pic: 'Admin', contact: 'N/A' },
                { id: 2, name: 'Sample Warehouse', pic: 'Sample Person', contact: '09695163' },
                { id: 3, name: 'Sample Warehouse 2', pic: 'sample person', contact: '34566789' },
            ]);
        }
    };

    const fetchStock = async () => {
        try {
            let endpoint = '';
            switch (itemType) {
                case 'Machine':
                    endpoint = '/stock/machines';
                    break;
                case 'Consumable':
                    endpoint = '/stock/consumables';
                    break;
                case 'Spare Part':
                    endpoint = '/stock/spare-parts';
                    break;
                case 'Demo Unit':
                    endpoint = '/stock/demo-units';
                    break;
                case 'Accessory':
                    endpoint = '/stock/accessories';
                    break;
                default:
                    endpoint = '/stock/machines';
            }

            const response = await axiosInstance.get(endpoint);
            const allItems = response.data;

            // Filter Logic
            let filteredItems = allItems;

            // 1. Filter by Warehouse (client-side as API returns all)
            if (sourceWarehouse) {
                filteredItems = filteredItems.filter(item => 
                    item.warehouse_id && item.warehouse_id.toString() === sourceWarehouse
                );
            }

            // 2. Map to unified structure to ensure consistent field names across different endpoints
            let mappedItems = filteredItems.map(item => ({
                ...item,
                brand: item.brand || '-',
                model: item.model || '-',
                description: item.itemDescription || item.item_description || item.description || '-',
                partNumber: item.itemCode || item.item_code || item.partNumber || item.part_number || '-',
                quantity: item.quantity || item.stock_quantity || 0,
                serial: item.serialNumber || item.serial_number || '-',
                type: item.type || itemType // Ensure type is set, defaulting to current tab
            }));

            // Extract unique brands for the current tab
            const uniqueBrands = [...new Set(mappedItems.map(item => item.brand).filter(brand => brand && brand !== '-'))].sort();
            setAvailableBrands(uniqueBrands);

            // Filter by Brand if a specific brand is selected
            if (selectedBrand !== 'all') {
                mappedItems = mappedItems.filter(item => item.brand === selectedBrand);
            }

            // 3. Filter by Search Query
            if (searchQuery.trim() !== '') {
                const query = searchQuery.toLowerCase().trim();
                mappedItems = mappedItems.filter(item => 
                    (item.brand && item.brand.toLowerCase().includes(query)) ||
                    (item.model && item.model.toLowerCase().includes(query)) ||
                    (item.description && item.description.toLowerCase().includes(query)) ||
                    (item.partNumber && item.partNumber.toLowerCase().includes(query)) ||
                    (item.serial && item.serial.toLowerCase().includes(query))
                );
            }

            // 4. Sort
            if (sortBy === 'alphabetical') {
                mappedItems.sort((a, b) => (a.brand || '').localeCompare(b.brand || ''));
            } else if (sortBy === 'quantity') {
                mappedItems.sort((a, b) => (b.quantity || 0) - (a.quantity || 0));
            }

            // 5. Aggregation Logic
            const aggregatedItems = Object.values(mappedItems.reduce((acc, item) => {
                const key = `${item.brand}-${item.model}-${item.description}-${item.partNumber}`;
                if (!acc[key]) {
                    acc[key] = {
                        ...item,
                        quantity: 0,
                        items: [] // Store individual items here
                    };
                }
                acc[key].quantity += (item.quantity || 0);
                acc[key].items.push(item);
                return acc;
            }, {}));

            setAvailableStock(aggregatedItems);
        } catch (error) {
            console.error("Error fetching stock:", error);
            toast.error("Failed to fetch stock data");
            setAvailableStock([]);
        }
    };

    // --- Handlers ---
    const handleWarehouseClick = (warehouseId) => {
        if (sourceWarehouse === warehouseId.toString()) return;
        
        setIsTransitioning(true);
        setTimeout(() => {
            setSourceWarehouse(warehouseId.toString());
            setIsTransitioning(false);
        }, 300);
    };

    const addToTransferList = (item, qty, serials = []) => {
        setTransferList(prevList => {
            // Check if item already exists in transfer list (for aggregation)
            const existingItemIndex = prevList.findIndex(i => 
                i.brand === item.brand && 
                i.model === item.model && 
                i.partNumber === item.partNumber &&
                i.description === item.description
            );

            if (existingItemIndex >= 0) {
                const updatedList = [...prevList];
                const existingItem = updatedList[existingItemIndex];
                
                // If machine, merge serials
                if (item.type === 'Machine') {
                    // Update available serials in case more were loaded or passed
                    // We prefer the 'item.items' if available as it contains all source serials
                    const allSourceSerials = item.items || existingItem.allAvailableSerials || [];
                    
                    let updatedSelectedItems = existingItem.selectedItems || [];
                    
                    // If serials are passed, merge them
                    if (serials.length > 0) {
                        const existingSerialIds = updatedSelectedItems.map(s => s.id || s.serial);
                        const newUniqueSerials = serials.filter(s => !existingSerialIds.includes(s.id || s.serial));
                        
                        if (newUniqueSerials.length === 0) {
                             toast.warning("Selected serials already in transfer list");
                             return prevList;
                        }
                        updatedSelectedItems = [...updatedSelectedItems, ...newUniqueSerials];
                    }

                    updatedList[existingItemIndex] = {
                        ...existingItem,
                        transferQuantity: updatedSelectedItems.length, 
                        selectedItems: updatedSelectedItems,
                        allAvailableSerials: allSourceSerials
                    };
                } else {
                    // Non-machine, just add qty
                    updatedList[existingItemIndex] = {
                        ...existingItem,
                        transferQuantity: existingItem.transferQuantity + qty
                    };
                }
                toast.success(`Updated ${item.model} in transfer list`);
                return updatedList;
            } else {
                // Add new item
                toast.success(`Added ${qty} unit(s) of ${item.model} to transfer list`);
                return [...prevList, {
                    id: Date.now(), // Temporary ID for list
                    ...item,
                    transferQuantity: qty,
                    selectedItems: serials,
                    allAvailableSerials: item.items || [], // Store all source serials
                    destinationId: destWarehouse,
                    transferDate: transferDate
                }];
            }
        });
    };

    const handleItemClick = (item) => {
        if (item.type === 'Machine') {
            setSelectedItem(item);
            setTransferQty(1);
            setSelectedSerials([]); // Reset selection
            setIsTransferModalOpen(true);
        } else {
            // Directly add non-machine items
            addToTransferList(item, 1);
        }
    };

    const handleTabChange = (tab) => {
        if (itemType === tab) return;
        setIsTransitioning(true);
        setItemType(tab);
        // The transition duration should match the CSS duration
        setTimeout(() => {
            setIsTransitioning(false);
        }, 300);
    };

    const updateTransferListQty = (id, newQty) => {
        setTransferList(prevList => prevList.map(item => {
            if (item.id === id) {
                // For non-machines only
                if (item.type !== 'Machine') {
                     // Ensure qty doesn't exceed available source stock
                     const maxQty = item.quantity; // item.quantity is the total source stock for this group
                     const validQty = Math.max(1, Math.min(newQty, maxQty));
                     return { ...item, transferQuantity: validQty };
                }
            }
            return item;
        }));
    };

    const updateTransferListSerials = (itemId, serialObj, isChecked) => {
        setTransferList(prevList => prevList.map(item => {
            if (item.id === itemId) {
                let currentSelected = item.selectedItems || [];
                let newSelected;
                
                if (isChecked) {
                    // Add if not present
                    if (!currentSelected.find(s => (s.id || s.serial) === (serialObj.id || serialObj.serial))) {
                        newSelected = [...currentSelected, serialObj];
                    } else {
                        newSelected = currentSelected;
                    }
                } else {
                    // Remove
                    newSelected = currentSelected.filter(s => (s.id || s.serial) !== (serialObj.id || serialObj.serial));
                }
                
                return {
                    ...item,
                    selectedItems: newSelected,
                    transferQuantity: newSelected.length
                };
            }
            return item;
        }));
    };

    const handleConfirmTransfer = async () => {
        // Logic moved to handleFinalizeTransfer
        // This function now adds to list
        // Note: Destination and Date are now global, not in modal
        if (!destWarehouse) {
            toast.error("Please select a destination warehouse");
            return;
        }
        if (sourceWarehouse === destWarehouse) {
            toast.error("Source and Destination warehouses cannot be the same");
            return;
        }
        if (!selectedItem) return;

        // If multiple items (Machines with grouped serials), check if serials are selected
        if (selectedItem.items && selectedItem.items.length > 1 && selectedSerials.length === 0) {
             toast.error("Please select at least one serial number to transfer");
             return;
        }

        // Determine items to transfer
        let itemsToTransfer = [];
        let transferQuantity = 0;

        if (selectedItem.items && selectedItem.items.length > 1) {
            // Transfer selected serials
            itemsToTransfer = selectedItem.items.filter(i => selectedSerials.includes(i.id || i.serial));
            transferQuantity = itemsToTransfer.length;
        } else {
            // Single item transfer
            // Prefer the item from the items array if available (cleaner object), otherwise fallback to selectedItem
            const singleItem = (selectedItem.items && selectedItem.items.length > 0) 
                ? selectedItem.items[0] 
                : selectedItem;
            itemsToTransfer = [singleItem];
            transferQuantity = 1; 
        }

        // Add to transferList
        addToTransferList(selectedItem, transferQuantity, itemsToTransfer);
        
        setIsTransferModalOpen(false);
        setSelectedItem(null);
        setTransferQty(1);
        setSelectedSerials([]);
    };

    const handleRemoveFromList = (id) => {
        setTransferList(transferList.filter(item => item.id !== id));
    };

    const handleFinalizeTransfer = async () => {
        if (transferList.length === 0) {
            toast.error("Transfer list is empty");
            return;
        }

        if (!destWarehouse) {
            toast.error("Please select a destination warehouse");
            return;
        }

        try {
            const itemsPayload = [];
        
            transferList.forEach(item => {
                if (item.type === 'Machine') {
                     itemsPayload.push({
                         stock_id: item.id,
                         quantity: item.transferQuantity,
                         serial_ids: item.selectedItems ? item.selectedItems.map(s => s.id || s.serial) : [],
                         type: item.type
                     });
                } else {
                     // De-aggregate: distribute transfer quantity across source records
                     let remaining = item.transferQuantity;
                     const sourceRecords = item.items || [item];
                     
                     for (const record of sourceRecords) {
                         if (remaining <= 0) break;
                         const available = record.quantity;
                         const take = Math.min(remaining, available);
                         
                         itemsPayload.push({
                             stock_id: record.id,
                             quantity: take,
                             serial_ids: [],
                             type: item.type
                         });
                         
                         remaining -= take;
                     }
                }
            });

            const payload = {
                source_warehouse_id: sourceWarehouse,
                destination_warehouse_id: destWarehouse,
                transfer_date: transferDate,
                items: itemsPayload
            };

            await axiosInstance.post('/stock/transfer', payload);

            toast.success("Stock transfer completed successfully");
            setTransferList([]);
            setDestWarehouse('');
            
            // Refresh stock to simulate update (in a real app, this fetches new data)
            fetchStock();
        } catch (error) {
            console.error("Transfer failed:", error);
            toast.error(error.response?.data?.detail || "Transfer failed");
        }
    };

    // Live update source table
    // availableStock state holds the raw fetched data. 
    // We can compute the "Remaining Display Qty" during render.
    
    // Helper to get remaining quantity
    const getRemainingQuantity = (item) => {
        const transferItem = transferList.find(t => 
            t.brand === item.brand && 
            t.model === item.model && 
            t.partNumber === item.partNumber
        );
        const transferredQty = transferItem ? transferItem.transferQuantity : 0;
        return Math.max(0, item.quantity - transferredQty);
    };

    // Helper to get warehouse name
    const getSelectedWarehouseName = () => {
        const warehouse = warehouses.find(w => w.id.toString() === sourceWarehouse);
        return warehouse ? warehouse.name : 'Warehouse';
    };

    return (
        <div className="p-6 h-full flex flex-col space-y-6">
            <div className="flex items-center gap-2">
                <ArrowRightLeft className="h-6 w-6 text-orange-600" />
                <h2 className="text-2xl font-bold text-gray-800">Stock Transfer</h2>
            </div>

            <div className="flex-1 flex gap-6 h-full overflow-hidden">
                {/* --- LEFT SIDEBAR (Warehouses List) --- */}
                <div className="w-1/3 bg-white p-4 rounded-lg shadow flex flex-col h-full overflow-hidden">
                    <h3 className="font-semibold text-lg text-gray-700 mb-4">Warehouses</h3>
                    
                    <div className="flex-1 overflow-y-auto space-y-3 pr-2">
                        {warehouses.map(w => (
                            <div 
                                key={w.id}
                                onClick={() => handleWarehouseClick(w.id)}
                                className={`p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                                    sourceWarehouse === w.id.toString() 
                                        ? 'border-orange-500 bg-orange-50 ring-1 ring-orange-500' 
                                        : 'border-gray-200 bg-white'
                                }`}
                            >
                                <div className="flex justify-between items-start">
                                    <h4 className="font-semibold text-gray-800">{w.name}</h4>
                                    {/* Mock Edit/Delete Icon placeholder if needed */}
                                </div>
                                <div className="mt-2 text-sm text-gray-500 space-y-1">
                                    <p>PIC: <span className="text-gray-700">{w.pic}</span></p>
                                    <p>Contact: <span className="text-gray-700">{w.contact}</span></p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* --- RIGHT PANEL (Inventory) --- */}
                <div className={`flex-1 bg-white p-4 rounded-lg shadow flex flex-col h-full overflow-hidden transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
                    
                    {/* Source Inventory Header */}
                    {sourceWarehouse && (
                        <h3 className="text-xl font-bold text-gray-800 mb-4">{getSelectedWarehouseName()} Inventory</h3>
                    )}

                    {/* Tabs */}
                    <div className="flex bg-gray-100 rounded-lg p-1 mb-4">
                        {[
                          { value: 'Machine', label: 'Machines' },
                          { value: 'Consumable', label: 'Consumables' },
                          { value: 'Spare Part', label: 'Spare Parts' },
                          { value: 'Demo Unit', label: 'Demo Units' },
                          { value: 'Accessory', label: 'Accessories' },
                        ].map((tab) => (
                            <button
                                key={tab.value}
                                onClick={() => handleTabChange(tab.value)}
                                className={`flex-1 py-2 text-sm font-medium rounded-md transition-all duration-300 ease-in-out ${
                                    itemType === tab.value
                                        ? 'bg-white text-orange-600 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Filters Row */}
                    <div className="grid grid-cols-12 gap-4 mb-4 items-end">
                        <div className="col-span-3 space-y-1">
                            <Label className="text-xs text-gray-500">Brand</Label>
                            <Select value={selectedBrand} onValueChange={setSelectedBrand}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select Brand" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Brands</SelectItem>
                                    {availableBrands.map(brand => (
                                        <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        
                        <div className="col-span-6 space-y-1">
                            <Label className="text-xs text-gray-500">Search</Label>
                            <div className="relative">
                                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                                <Input 
                                    placeholder="Search Model, Description, Item Code, or Serial..." 
                                    className="pl-8"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="col-span-3 space-y-1">
                            <Label className="text-xs text-gray-500">Sort By</Label>
                            <Select value={sortBy} onValueChange={setSortBy}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Sort By" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="alphabetical">Alphabetical (A-Z)</SelectItem>
                                    <SelectItem value="quantity">Quantity (High-Low)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Inventory Table */}
                    <div className="flex-1 overflow-auto border rounded-md mb-8">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-gray-50">
                                    <TableHead>Brand</TableHead>
                                    <TableHead>Model</TableHead>
                                    <TableHead>Item Description</TableHead>
                                    <TableHead>Item Code</TableHead>
                                    <TableHead>Quantity</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {availableStock.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center text-gray-500 py-12">
                                            {sourceWarehouse 
                                                ? "No items found matching your filters." 
                                                : "Select a warehouse from the left to view inventory."}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    availableStock.map((item) => {
                                        const remainingQty = getRemainingQuantity(item);
                                        return (
                                            <TableRow 
                                                key={item.brand + item.model + item.partNumber} 
                                                className={`cursor-pointer transition-colors hover:bg-orange-600 hover:text-white ${remainingQty === 0 ? 'opacity-50 pointer-events-none' : ''}`}
                                                onClick={() => remainingQty > 0 && handleItemClick(item)}
                                            >
                                                <TableCell className="font-medium">{item.brand}</TableCell>
                                                <TableCell>{item.model}</TableCell>
                                                <TableCell>{item.description}</TableCell>
                                                <TableCell>{item.partNumber}</TableCell>
                                                <TableCell>{remainingQty}</TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    {/* --- DESTINATION WAREHOUSE (TRANSFER LIST) --- */}
                    <div className="border-t pt-8 mt-8">
                         <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-4">
                                <h3 className="text-lg font-bold text-gray-800">Transfer To:</h3>
                                <div className="w-64">
                                    <Select value={destWarehouse} onValueChange={setDestWarehouse}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select Destination Warehouse" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {warehouses
                                                .filter(w => w.id.toString() !== sourceWarehouse)
                                                .map(w => (
                                                    <SelectItem key={w.id} value={w.id.toString()}>{w.name}</SelectItem>
                                                ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            
                            <Button 
                                className="bg-orange-600 hover:bg-orange-700 text-white"
                                onClick={handleFinalizeTransfer}
                                disabled={transferList.length === 0}
                            >
                                Confirm Transfer ({transferList.length})
                            </Button>
                        </div>

                        {/* Transfer List Table */}
                        <div className="border rounded-md overflow-hidden">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-gray-50">
                                        <TableHead>Brand</TableHead>
                                        <TableHead>Model</TableHead>
                                        <TableHead>Item Description</TableHead>
                                        <TableHead>Item Code / Part #</TableHead>
                                        <TableHead>Serial #</TableHead>
                                        <TableHead>Transfer Qty</TableHead>
                                        <TableHead className="text-right">Action</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {transferList.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} className="text-center text-gray-500 py-8">
                                                No items added to transfer list. Select items from the inventory above.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        transferList.map((item) => (
                                            <TableRow key={item.id}>
                                                <TableCell className="font-medium">{item.brand}</TableCell>
                                                <TableCell>{item.model}</TableCell>
                                                <TableCell>{item.description}</TableCell>
                                                <TableCell>{item.partNumber}</TableCell>
                                                <TableCell>
                                                    {item.type === 'Machine' ? (
                                                        item.transferQuantity > 1 ? (
                                                            <DropdownMenu>
                                                                <DropdownMenuTrigger asChild>
                                                                    <Button variant="outline" size="sm" className="h-8 border-dashed">
                                                                        {item.transferQuantity} Selected <ChevronDown className="ml-2 h-4 w-4" />
                                                                    </Button>
                                                                </DropdownMenuTrigger>
                                                                <DropdownMenuContent className="w-56" align="start">
                                                                    <DropdownMenuLabel>Select Serials</DropdownMenuLabel>
                                                                    <DropdownMenuSeparator />
                                                                    <div className="max-h-60 overflow-y-auto">
                                                                        {(item.allAvailableSerials || item.selectedItems || []).map((serialObj) => {
                                                                            const isChecked = (item.selectedItems || []).some(s => (s.id || s.serial) === (serialObj.id || serialObj.serial));
                                                                            return (
                                                                                <DropdownMenuCheckboxItem
                                                                                    key={serialObj.id || serialObj.serial}
                                                                                    checked={isChecked}
                                                                                    onCheckedChange={(checked) => updateTransferListSerials(item.id, serialObj, checked)}
                                                                                >
                                                                                    {serialObj.serial || serialObj.id}
                                                                                </DropdownMenuCheckboxItem>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </DropdownMenuContent>
                                                            </DropdownMenu>
                                                        ) : (
                                                            <span className="text-gray-700 font-medium">
                                                                {item.selectedItems && item.selectedItems[0] ? (item.selectedItems[0].serial || item.selectedItems[0].id) : '-'}
                                                            </span>
                                                        )
                                                    ) : (
                                                        <span className="text-gray-400">-</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {item.type !== 'Machine' ? (
                                                        <Input 
                                                            type="number" 
                                                            className="w-20 h-8"
                                                            value={item.transferQuantity}
                                                            min={1}
                                                            onChange={(e) => updateTransferListQty(item.id, parseInt(e.target.value) || 0)}
                                                        />
                                                    ) : (
                                                        item.transferQuantity
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <Button 
                                                        size="sm" 
                                                        variant="ghost" 
                                                        className="text-red-500 hover:text-red-700"
                                                        onClick={() => handleRemoveFromList(item.id)}
                                                    >
                                                        Remove
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </div>

                </div>
            </div>

            {/* --- Stock Transfer Modal --- */}
            <Dialog open={isTransferModalOpen} onOpenChange={setIsTransferModalOpen}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle>Stock Transfer</DialogTitle>
                    </DialogHeader>
                    
                    {selectedItem && (
                        <div className="space-y-4 py-4">
                            <div className="bg-gray-50 p-3 rounded-md space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Item:</span>
                                    <span className="font-medium">{selectedItem.brand} {selectedItem.model}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Current Stock:</span>
                                    <span className="font-medium">{selectedItem.quantity}</span>
                                </div>
                                {/* Show serial only if single item */}
                                {(!selectedItem.items || selectedItem.items.length <= 1) && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Serial:</span>
                                        <span className="font-medium">{selectedItem.serial}</span>
                                    </div>
                                )}
                            </div>

                            {/* Serial Number Selection for Grouped Items */}
                            {selectedItem.items && selectedItem.items.length > 1 ? (
                                <div className="space-y-2">
                                    <Label>Select Serial Number(s)</Label>
                                    <Select 
                                        value={selectedSerials.length === 1 ? selectedSerials[0] : undefined} 
                                        onValueChange={(val) => setSelectedSerials([val])} 
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select a serial number" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {selectedItem.items.map(item => (
                                                <SelectItem key={item.id || item.serial} value={item.id || item.serial}>
                                                    {item.serial || 'No Serial'}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <p className="text-xs text-gray-500">
                                        * To transfer multiple, please select one at a time
                                    </p>
                                </div>
                            ) : (
                                <p className="text-sm text-gray-500">This machine will be added to transfer list.</p>
                            )}
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsTransferModalOpen(false)}>Cancel</Button>
                        <Button className="bg-orange-600 hover:bg-orange-700" onClick={handleConfirmTransfer}>
                            Confirm Transfer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default StockTransfer;
